import React from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { AlertTriangle, RotateCcw, PlusCircle } from 'lucide-react'

interface DeletedDuplicateMatch {
	name: string
	id: string
}

interface DeletedDuplicateDialogProps {
	open: boolean
	onClose: () => void
	itemType: 'task' | 'site detail'
	matches: DeletedDuplicateMatch[]
	onRestore: () => void
	onCreateNew: () => void
	isLoading?: boolean
}

const DeletedDuplicateDialog = ({
	open,
	onClose,
	itemType,
	matches,
	onRestore,
	onCreateNew,
	isLoading = false,
}: DeletedDuplicateDialogProps) => {
	const isBatch = matches.length > 1
	const itemTypeLabel = itemType === 'task' ? 'task' : 'site detail'
	const itemTypeLabelPlural = itemType === 'task' ? 'tasks' : 'site details'

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent>
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-100 text-amber-600">
							<AlertTriangle className="w-5 h-5" />
						</div>
						<div>
							<DialogTitle>
								Previously Deleted {isBatch ? itemTypeLabelPlural : itemTypeLabel} Found
							</DialogTitle>
							<DialogDescription>
								{isBatch
									? `${matches.length} ${itemTypeLabelPlural} from this template match previously deleted items.`
									: `A ${itemTypeLabel} with this name was previously deleted.`}
							</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<div className="px-5 py-4">
					{isBatch ? (
						<>
							<p className="text-sm text-gray-600 mb-3">
								The following {itemTypeLabelPlural} match previously deleted items:
							</p>
							<ul className="space-y-1.5 mb-4 max-h-48 overflow-y-auto">
								{matches.map((match, i) => (
									<li
										key={match.id || i}
										className="flex items-center gap-2 text-sm text-gray-800 bg-gray-50 rounded-md px-3 py-1.5"
									>
										<span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
										{match.name}
									</li>
								))}
							</ul>
						</>
					) : (
						<p className="text-sm text-gray-600 mb-4">
							A {itemTypeLabel} named <span className="font-semibold text-gray-900">"{matches[0]?.name}"</span> was
							previously deleted. Would you like to restore the existing item or create a new one?
						</p>
					)}

					<p className="text-xs text-gray-500">
						{isBatch
							? `Items without a deleted match will be created normally regardless of your choice.`
							: `Restoring will un-delete the original ${itemTypeLabel}. Creating new will add a separate item.`}
					</p>
				</div>

				<DialogFooter className="px-5 pb-5 border-t border-gray-200 pt-4">
					<Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
						Cancel
					</Button>
					<Button
						type="button"
						variant="ghost"
						isOutline
						onClick={onRestore}
						disabled={isLoading}
						className="gap-1.5"
					>
						<RotateCcw className="w-4 h-4" />
						{isBatch ? 'Restore All Existing' : 'Restore Existing'}
					</Button>
					<Button type="button" onClick={onCreateNew} disabled={isLoading} className="gap-1.5">
						<PlusCircle className="w-4 h-4" />
						{isBatch ? 'Create All New' : 'Create New'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default DeletedDuplicateDialog
